<template>
  <v-dialog v-model="dialog" persistent :max-width="$vuetify.breakpoint.mobile ? '95vw' : '520'">
    <v-card style="position: relative;">
      <v-toolbar color="primary" elevation="0">
        <v-toolbar-title class="white--text">{{ $t('dueDiligence.paymentResponsibilityTitle') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" icon @click="closeModal" :disabled="!!submitting">
          <v-icon style="color:white !important">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-overlay :value="!!submitting" absolute opacity="0.55" z-index="6">
        <div class="d-flex flex-column align-center white--text">
          <v-progress-circular indeterminate color="white" size="48" class="mb-3"></v-progress-circular>
          <div class="text-center">{{ submittingMessage }}</div>
        </div>
      </v-overlay>

      <v-card-text class="pt-5 black--text">
        <p class="mb-4">{{ $t('dueDiligence.paymentResponsibilityDescription') }}</p>

        <div class="summary-box pa-4">
          <div class="d-flex justify-space-between mb-2">
            <span class="grey--text">{{ $t('dueDiligence.ddsReferenceNo') }}</span>
            <span class="font-weight-medium">{{ item && item.internalReferenceNumber || '-' }}</span>
          </div>
          <div class="d-flex justify-space-between mb-2">
            <span class="grey--text">{{ $t('deforestation.product') }}</span>
            <span class="font-weight-medium">{{ item && item.product_detail ? item.product_detail.name : '-' }}</span>
          </div>
          <div class="d-flex justify-space-between mb-2">
            <span class="grey--text">{{ $t('dueDiligence.quantityTons') }}</span>
            <span class="font-weight-medium">{{ formattedQuantity }}</span>
          </div>
          <div class="d-flex justify-space-between mb-2">
            <span class="grey--text">{{ $t('dueDiligence.pricePerTon') }}</span>
            <span class="font-weight-medium">{{ formattedPricePerTon }}</span>
          </div>
          <div class="d-flex justify-space-between">
            <span class="grey--text">{{ $t('dueDiligence.paymentAmountDue') }}</span>
            <span class="font-weight-bold">{{ formattedAmount }}</span>
          </div>
        </div>

        <div class="wire-transfer-note d-flex align-start mt-4 pa-3">
          <v-icon color="primary" class="mr-2" small>mdi-bank-transfer</v-icon>
          <span class="text-body-2">{{ $t('dueDiligence.paymentResponsibilityWireTransferNote') }}</span>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="error"
          @click="respond(false)"
          :loading="submitting === 'decline'"
          :disabled="!!submitting"
        >
          {{ $t('dueDiligence.declinePaymentResponsibility') }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          @click="respond(true)"
          :loading="submitting === 'accept'"
          :disabled="!!submitting"
        >
          {{ $t('dueDiligence.acceptPaymentResponsibility') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'PaymentResponsibilityModal',
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      submitting: null,
    }
  },
  computed: {
    formattedAmount() {
      const payment = this.item?.payment;
      if (!payment || payment.amount == null) return '-';
      const currency = payment.currency || '';
      return `${currency} ${Number(payment.amount).toLocaleString()}`.trim();
    },
    formattedQuantity() {
      const netMass = this.item?.productNetMass;
      if (netMass == null || isNaN(netMass)) return '-';
      const tons = Number(netMass) / 1000;
      return `${tons.toLocaleString()} t`;
    },
    netMassTons() {
      const netMass = this.item?.productNetMass;
      if (netMass == null || isNaN(netMass)) return null;
      return Number(netMass) / 1000;
    },
    pricePerTon() {
      const payment = this.item?.payment;
      if (payment?.price_per_ton_snapshot != null) return Number(payment.price_per_ton_snapshot);
      if (payment?.amount != null && this.netMassTons) return Number(payment.amount) / this.netMassTons;
      return null;
    },
    formattedPricePerTon() {
      if (this.pricePerTon == null) return '-';
      const currency = this.item?.payment?.currency || '';
      return `${currency} ${this.pricePerTon.toLocaleString()}`.trim();
    },
    submittingMessage() {
      if (this.submitting === 'decline') {
        return this.$t('dueDiligence.decliningPaymentResponsibility');
      }
      return this.$t('dueDiligence.acceptingPaymentResponsibility');
    },
  },
  methods: {
    closeModal() {
      if (this.submitting) return;
      this.resetSubmitting();
      this.$emit('close');
    },
    resetSubmitting() {
      this.submitting = null;
    },
    respond(accept) {
      if (this.submitting) return;
      this.submitting = accept ? 'accept' : 'decline';
      // Keep loader until parent finishes API, then close/reset via done().
      this.$emit('respond', {
        accept,
        done: (error) => {
          this.resetSubmitting();
          if (!error) {
            this.$emit('close');
          }
        },
      });
    }
  },
  watch: {
    dialog(newVal) {
      if (!newVal) {
        this.resetSubmitting();
      }
    }
  }
}
</script>

<style scoped>
.summary-box {
  background: #f8f9fa;
  border-radius: 8px;
  line-height: 1.6;
}

.wire-transfer-note {
  background: #f3f8ff;
  border: 1px solid #d6e4f5;
  border-radius: 8px;
  color: #334155;
  line-height: 1.5;
}
</style>
